import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronDown, faChevronUp, faCheckCircle, faExclamationCircle, faSpinner, faClock, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import './DocumentsListView.css';

const UploadQueueModal = ({ isOpen, onClose, uploadQueue, onClearCompleted, onConfirmDuplicate, onClearHistory }) => {
  const [completedSectionExpanded, setCompletedSectionExpanded] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());

  if (!isOpen) return null;

  const inProgressItems = uploadQueue.filter(item => item.status === 'queued' || item.status === 'uploading' || item.status === 'duplicate');
  const completedItems = uploadQueue.filter(item => item.status === 'complete' || item.status === 'error');

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getTimeSince = (timestamp) => {
    const minutes = Math.floor((Date.now() - timestamp) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    return `${hours} hours ago`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <FontAwesomeIcon icon={faCheckCircle} className="upload-status-icon upload-status-complete" />;
      case 'error':
        return <FontAwesomeIcon icon={faExclamationCircle} className="upload-status-icon upload-status-error" />;
      case 'uploading':
        return <FontAwesomeIcon icon={faSpinner} spin className="upload-status-icon upload-status-uploading" />;
      case 'queued':
        return <FontAwesomeIcon icon={faClock} className="upload-status-icon upload-status-queued" />;
      case 'duplicate':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="upload-status-icon upload-status-duplicate" />;
      default:
        return null;
    }
  };

  const getStatusText = (item) => {
    switch (item.status) {
      case 'complete':
        return item.completionTime ? getTimeSince(item.completionTime) : 'Complete';
      case 'error':
        return item.completionTime ? `Failed ${getTimeSince(item.completionTime)}` : 'Upload failed';
      case 'uploading':
        return 'Uploading...';
      case 'queued':
        return 'Queued...';
      case 'duplicate':
        return 'Duplicate detected';
      default:
        return '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1000) return `${bytes} B`;
    if (bytes < 1000000) return `${(bytes / 1000).toFixed(1)} KB`;
    return `${(bytes / 1000000).toFixed(2)} MB`;
  };

  const handleClose = () => {
    onClose();
  };

  const renderCompletedOrErrorDetails = (item) => {
    const isExpanded = expandedItems.has(item.id);
    return (
      <div className="upload-history-section">
        <div
          className="upload-history-expand-row"
          onClick={() => toggleExpanded(item.id)}
        >
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="upload-history-expand-icon"
          />
        </div>
        {isExpanded && (
          <div className="upload-history-details">
            {item.completionTime && (
              <div className="upload-history-detail-row">
                <span className="upload-history-detail-label">
                  {item.status === 'complete' ? 'Uploaded' : 'Failed'}
                </span>
                <span className="upload-history-detail-value">{getTimeSince(item.completionTime)}</span>
              </div>
            )}
            <div className="upload-history-detail-row">
              <span className="upload-history-detail-label">File size</span>
              <span className="upload-history-detail-value">{formatFileSize(item.fileSize)}</span>
            </div>
            {item.status === 'error' && item.errorMessage && (
              <div className="upload-history-detail-row upload-history-detail-error">
                <span className="upload-history-detail-label">Error</span>
                <span className="upload-history-detail-value">{item.errorMessage}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="upload-queue-modal">
        <div className="modal-header">
          <h2>Document Uploads</h2>
          <button className="close-button" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="upload-queue-content">
          {inProgressItems.length === 0 && completedItems.length === 0 ? (
            <div className="upload-queue-empty">
              <p>No uploads in progress</p>
            </div>
          ) : (
            <>
              {inProgressItems.length > 0 && (
                <div className="upload-queue-section">
                  <div className="upload-queue-section-header">
                    <h3>In Progress ({inProgressItems.length})</h3>
                  </div>
                  <div className="upload-queue-list">
                    {inProgressItems.map(item => (
                      <div key={item.id} className={`upload-item upload-item-${item.status}`}>
                        <div className="upload-item-header">
                          <div className="upload-item-info">
                            {getStatusIcon(item.status)}
                            <span className="upload-item-filename">{item.fileName}</span>
                          </div>
                          <div className="upload-item-status">
                            {getStatusText(item)}
                          </div>
                        </div>
                        {(item.status === 'uploading' || item.status === 'queued') && (
                          <div className="upload-progress-container">
                            <div className="upload-progress-bar">
                              <div 
                                className="upload-progress-fill" 
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                            <span className="upload-progress-text">{Math.round(item.progress)}%</span>
                          </div>
                        )}
                        {item.status === 'duplicate' && (
                          <div className="upload-duplicate-section">
                            <div 
                              className="upload-duplicate-header"
                              onClick={() => toggleExpanded(item.id)}
                            >
                              <span>View previous upload details</span>
                              <FontAwesomeIcon 
                                icon={expandedItems.has(item.id) ? faChevronUp : faChevronDown} 
                              />
                            </div>
                            {expandedItems.has(item.id) && item.duplicateInfo && (
                              <div className="upload-duplicate-details">
                                <p><strong>Previously uploaded:</strong> {getTimeSince(item.duplicateInfo.timestamp)}</p>
                                <p><strong>Status:</strong> {item.duplicateInfo.status === 'complete' ? 'Success' : 'Failed'}</p>
                                <p><strong>File size:</strong> {formatFileSize(item.duplicateInfo.fileSize)}</p>
                                <button 
                                  className="upload-confirm-duplicate-button"
                                  onClick={() => onConfirmDuplicate(item.id)}
                                >
                                  Upload Anyway
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {completedItems.length > 0 && (
                <div className="upload-queue-section upload-completed-section">
                  <div 
                    className="upload-queue-section-header upload-queue-section-header-clickable"
                    onClick={() => setCompletedSectionExpanded(!completedSectionExpanded)}
                  >
                    <h3>Completed ({completedItems.length})</h3>
                    <FontAwesomeIcon 
                      icon={completedSectionExpanded ? faChevronUp : faChevronDown} 
                      className="upload-section-toggle"
                    />
                  </div>
                  {completedSectionExpanded && (
                    <div className="upload-queue-list">
                      {completedItems.map(item => (
                        <div key={item.id} className={`upload-item upload-item-${item.status}`}>
                          <div className="upload-item-header">
                            <div className="upload-item-info">
                              {getStatusIcon(item.status)}
                              <span className="upload-item-filename">{item.fileName}</span>
                            </div>
                            <div className="upload-item-status">
                              {getStatusText(item)}
                            </div>
                          </div>
                          {renderCompletedOrErrorDetails(item)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="upload-queue-footer">
          {inProgressItems.length > 0 ? (
            <p className="upload-queue-footer-text">
              Uploads will continue in the background when you close this window
            </p>
          ) : (
            <div className="upload-queue-footer-buttons">
              <button className="header-button" onClick={() => {
                onClearHistory();
                onClearCompleted();
              }}>
                Clear History
              </button>
              <button className="header-button" onClick={handleClose}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadQueueModal;
